'use client'

import { useWindowDimensions } from './use-window-dimensions'

/**
 * IMPORTANT: These hooks return `false` during SSR (when width is null).
 * This means on mobile devices, the server renders "desktop" content but
 * the client will switch to "mobile" content after hydration.
 *
 * To avoid hydration mismatches, prefer using CSS media queries for
 * show/hide behavior instead of conditional JS rendering.
 *
 * If you must conditionally render different components based on viewport:
 * 1. Use useHasMounted() to check if client has mounted
 * 2. Render a loading state or desktop version during SSR
 * 3. Only switch to mobile version after mount
 *
 * Example:
 *   const isMobile = useIsMobileView();
 *   const hasMounted = useHasMounted();
 *   // Render desktop version during SSR for consistency
 *   if (!hasMounted) return <DesktopVersion />;
 *   return isMobile ? <MobileVersion /> : <DesktopVersion />;
 */
export const useIsMobileView = () => {
  const { width } = useWindowDimensions(true)

  return width === null ? false : Boolean(width <= 767)
}

export const useIsMediumOrMobileView = () => {
  const { width } = useWindowDimensions(true)

  return width === null ? false : Boolean(width <= 991)
}

export const useIsLargeOrLess = () => {
  const { width } = useWindowDimensions()

  return width === null ? false : Boolean(width <= 1199)
}

export const useIsLaptopOrMore = () => {
  const { width } = useWindowDimensions()

  return width === null ? false : Boolean(width > 1199)
}

export const useIsWideScreenView = () => {
  const { width } = useWindowDimensions()

  return width === null ? false : Boolean(width >= 1600)
}

/**
 * Returns true once the component has mounted on the client.
 * Use this to safely perform client-only conditional rendering.
 */
export const useHasMounted = () => {
  const { hasMounted } = useWindowDimensions()
  return hasMounted
}
